import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarDays, Snowflake, Trophy } from 'lucide-react';
import { ChallengeMe, ChallengeSummary, challengeGoalModel, challengePhase, daysBetween, formatKmShort, participantTeaserModel, progressPercent, startInLabel } from '@radevents/shared';
import { ChallengeArt } from './Motion';
import './winterchallenge.css';

/** Flocken hinter dem Text, ruhig und blass. Position in Prozent der Karte, Größe in Pixeln. */
const TEASER_FLAKES: { right: string; top: string; size: number; opacity: number }[] = [
  { right: '3%', top: '-14%', size: 64, opacity: 0.07 },
  { right: '19%', top: '58%', size: 30, opacity: 0.09 },
  { right: '31%', top: '6%', size: 18, opacity: 0.12 },
  { right: '44%', top: '66%', size: 14, opacity: 0.1 },
];

/** Kurze Auskunft zur Phase: wie lange noch bis zum Start, welcher Tag der Saison, oder vorbei. */
function teaserChip(summary: ChallengeSummary, today: string): string {
  const phase = challengePhase(summary, today);
  if (phase === 'before') return startInLabel(summary, today);
  if (phase === 'after') return 'Saison beendet';
  return `Tag ${daysBetween(summary.startsOn, today) + 1} von ${daysBetween(summary.startsOn, summary.endsOn) + 1}`;
}

/**
 * Der Hinweis auf der Startseite, im Werbeplatz über der Liste (`WinterchallengePromoBanner`).
 * Eine helle Winterkarte: das Bild liegt ohne eigenen Kasten auf der Fläche (freigestellte
 * Fassung, Ränder weich ausgeblendet), dahinter ein paar Flocken. Der Titel der Saison steht als
 * Kicker, die Überschrift sagt, worum es geht; so steht „Winterchallenge“ nicht zweimal.
 *
 * `variant` statt Media-Query, weil der Werbeplatz Desktop und Handy getrennt zeichnet
 * (`hide-mobile`/`hide-desktop`) und jede Fassung ihre eigenen Bedienelemente bekommt.
 * Die ganze Fläche ist ein Link, die Blätterknöpfe liegen als Geschwister darüber wie bei
 * Delta Bike. Die feste Höhe im Werbeplatz kommt aus `PromoBannerSlot.css`.
 *
 * Wer schon mitmacht (`me`), sieht statt „Mitmachen“ die eigenen Kilometer, den Weg zum nächsten
 * Meilenstein und den Knopf zum Eintragen (`participantTeaserModel`). Dann sind es zwei Knöpfe: der
 * erste trägt ein (mit `onAddRide` im Schnellfenster auf der Startseite), „Rangliste“ und die übrige
 * Fläche führen zur Challenge-Seite mit den Ranglisten (Phil, 01.11.2026: vorher führte die ganze Karte zum
 * Eintragen, und zur Übersicht kam man von hier nicht mehr). Die Fläche ist ein gestreckter Link
 * (`wc-teaser-stretch`), die Knöpfe eigene Links darüber — kein Link im Link.
 */
export function ChallengeTeaser({ summary, today, me, variant = 'desktop', href = '/winterchallenge', onCta, onAddRide, controls }: {
  /** `null` = Stand noch nicht geladen: Titel und Knopf stehen trotzdem, damit nichts springt. */
  summary: ChallengeSummary | null;
  today: string;
  /** Eigene Teilnahme; `null`/fehlt = noch nicht dabei oder nicht angemeldet. */
  me?: ChallengeMe | null;
  variant?: 'desktop' | 'mobile';
  href?: string;
  onCta?: () => void;
  /** Teilnehmer während der Saison: öffnet das Schnellfenster statt die Challenge-Seite. */
  onAddRide?: () => void;
  /** Pfeile und Punkte des Werbeplatzes. */
  controls?: React.ReactNode;
}) {
  const phase = summary ? challengePhase(summary, today) : 'before';
  const mobile = variant === 'mobile';
  const people = summary ? summary.participants.toLocaleString('de-DE') : '';

  // Vor dem Start und während der Saison steht das gemeinsame Ziel oben (Phil, 25.10.2026: der Mond
  // schon vor dem Start). Vor dem Start steht darunter nichts: keine Zeile, kein Balken.
  const headlineGuest = !summary ? 'Kilometer sammeln, den ganzen Winter.'
    : phase !== 'after' ? challengeGoalModel(summary, today).headline
    : `${formatKmShort(summary.totalKm)} km. Danke fürs Mitfahren.`;
  // Vor dem Start steht am Handy „Start in N Tagen“ als Zeile, weil der Chip dort ausgeblendet ist
  // und die Karte sonst halb leer stünde; am Desktop trägt der Chip die Auskunft.
  const lineGuest: string | null = !summary ? 'Allein oder im Team, draußen oder auf der Rolle.'
    : phase === 'before' ? (mobile ? startInLabel(summary, today) : null)
    : phase === 'running'
      ? `${formatKmShort(summary.totalKm)} km gesammelt.`
    : `${people} sind mitgefahren.`;

  const mine = summary && me ? participantTeaserModel(me, summary, today) : null;
  const headline = mine ? mine.headline : headlineGuest;
  const line = mine ? mine.detail : lineGuest;
  const percent = mine ? mine.percent : summary && phase === 'running' ? progressPercent(summary.totalKm, summary.goalKm) : null;
  const cta = mine ? mine.action : phase === 'after' ? 'Ergebnisse ansehen' : 'Mitmachen';

  const className = `wc-teaser wc-teaser--${variant}${controls ? ' wc-teaser--slot' : ''}${line ? '' : ' wc-teaser--noline'}`;
  const inner = (
    <>
      {TEASER_FLAKES.slice(0, mobile ? 2 : 4).map((f, i) => (
        <Snowflake key={i} className="wc-teaser-flake" size={f.size} strokeWidth={1.5} aria-hidden
          style={{ right: f.right, top: f.top, opacity: f.opacity }} />
      ))}
      <ChallengeArt className="wc-teaser-art" free />
      <div className="wc-teaser-copy">
        <span className="wc-teaser-meta">
          <span className="wc-kicker">{summary?.title ?? 'Winterchallenge'}</span>
          {summary && <span className="wc-teaser-chip"><CalendarDays size={12} aria-hidden />{teaserChip(summary, today)}</span>}
        </span>
        <strong className="wc-teaser-title">{headline}</strong>
        {line && <span className="wc-teaser-line">{line}</span>}
        {percent != null && (
          <span className="wc-bar wc-bar--slim"><span className="wc-bar-fill" style={{ width: `${percent}%` }} /></span>
        )}
      </div>
    </>
  );
  const ctaContent = <>{cta}<ArrowRight size={16} aria-hidden /></>;

  if (mine?.addRide) {
    return (
      <div className="wc-teaser-wrap">
        <div className={`${className} wc-teaser--split`}>
          <Link to={href} onClick={() => onCta?.()} className="wc-teaser-stretch" aria-label="Zur Winterchallenge" />
          {inner}
          <span className="wc-teaser-actions">
            <Link to={mine.href} onClick={e => {
              onCta?.();
              // Mittelklick und Strg/Cmd-Klick öffnen den Link im neuen Tab, wie bei jedem Link.
              const plain = e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
              if (plain && onAddRide) { e.preventDefault(); onAddRide(); }
            }} className="wc-btn wc-teaser-cta wc-cue">
              {ctaContent}
            </Link>
            <Link to={href} onClick={() => onCta?.()} className="wc-btn wc-btn--ghost wc-teaser-cta wc-teaser-cta--second">
              <Trophy size={16} aria-hidden />Rangliste
            </Link>
          </span>
        </div>
        {controls}
      </div>
    );
  }

  return (
    <div className="wc-teaser-wrap">
      <Link to={mine ? mine.href : href} onClick={() => onCta?.()} className={className}>
        {inner}
        <span className="wc-btn wc-teaser-cta wc-cue">{ctaContent}</span>
      </Link>
      {controls}
    </div>
  );
}
